import { Timestamp } from 'firebase/firestore';
import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, Outlet, useNavigate, useSearchParams } from 'react-router-dom';
import { setPageTitle } from '@/utils/pageTitle';
import {
  ChevronLeft,
  Home,
  Menu,
  X,
  Search,
  Plus,
  FileSpreadsheet,
  Calendar,
  CheckCircle
} from 'lucide-react';
import { amazonIndexCheckerService } from '@/services/amazonIndexCheckerService';
import { SheetsConfig, ASINScheduleStatus } from '@/types/amazonIndexChecker';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/context/AuthContext';
import { employeeNamesService } from '@/services/employeeNames';
import { UserInfoSection } from '@/components/layout/UserInfoSection';
import { usePMT3Selection } from '@/context/PMT3SelectionContext';
import { useDarkMode } from '@/context/DarkModeContext';
import KeyboardNavigableSelect from '@/components/ui/KeyboardNavigableSelect';
import { pmt2Service } from '@/services/pmt2Service';

const AmazonIndexLayout: React.FC = () => {
  const [onPasteTermsClickRef, setOnPasteTermsClickRef] = useState<(() => void) | null>(null);
  const [onSheetsTermsClickRef, setOnSheetsTermsClickRef] = useState<(() => void) | null>(null);
  const [sheetsConfig, setSheetsConfig] = useState<SheetsConfig | null>(null);
  
  // ASIN scheduling state
  const [asinScheduleStatus, setAsinScheduleStatus] = useState<ASINScheduleStatus>({ isScheduled: false });
  const [isScheduleToggling, setIsScheduleToggling] = useState(false);
  const [allResultsCount, setAllResultsCount] = useState(0);
  
  // Reverse ASIN lookup state
  const [pasteAsin, setPasteAsin] = useState<string>('');
  const [asinValidationStatus, setAsinValidationStatus] = useState<'idle' | 'validating' | 'valid' | 'invalid'>('idle');
  const [lookupTimeout, setLookupTimeout] = useState<NodeJS.Timeout | null>(null);
  
  // Dashboard state
  const [isDashboardActive, setIsDashboardActive] = useState(true);

  // Memoize the callbacks to prevent unnecessary re-renders
  const handleSetOnPasteTermsClick = useCallback((callback: (() => void) | null) => {
    setOnPasteTermsClickRef(() => callback);
  }, []);

  const handleSetOnSheetsTermsClick = useCallback((callback: (() => void) | null) => {
    setOnSheetsTermsClickRef(() => callback);
  }, []);

  const handleDashboardStateChange = useCallback((isDashboard: boolean) => {
    setIsDashboardActive(isDashboard);
  }, []);

  const [displayName, setDisplayName] = useState<string>('');
  const location = useLocation();
  const navigate = useNavigate();
  const { userEmail } = useAuth();
  const { isDarkMode } = useDarkMode();
  const [isCollapsed, setIsCollapsed] = useState(window.innerWidth < 1024);
  const [isFullScreen, setIsFullScreen] = useState(window.innerWidth >= 1024);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // Use the PMT3Selection context for company and ASIN selection
  const {
    companies,
    selectedCompany,
    setSelectedCompany,
    products,
    selectedProduct,
    setSelectedProduct
  } = usePMT3Selection();
  
  // Check if we're in company-selected view (company selected but no ASIN in URL)
  const [searchParams] = useSearchParams();
  const isCompanyView = searchParams.get('view') === 'company' && selectedCompany && !searchParams.get('asin');
  
  // Clear selected product when in company view to prevent auto-selection
  useEffect(() => {
    if (isCompanyView && selectedProduct) {
      setSelectedProduct(null);
    }
  }, [isCompanyView, selectedProduct, setSelectedProduct]);

  useEffect(() => {
    const loadDisplayName = async () => {
      if (userEmail) {
        const name = await employeeNamesService.getNameFromEmail(userEmail);
        if (name) {
          setDisplayName(name);
        }
      }
    };
    loadDisplayName();
  }, [userEmail]);

  // Handle window resize for responsive layout
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setWindowWidth(width);
      setIsFullScreen(width >= 1024);
      
      // Only allow collapsing if not in full screen mode
      if (width >= 1024) {
        setIsCollapsed(false);
      }
    };
    
    window.addEventListener('resize', handleResize);
    handleResize(); // Initial check
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Set page title
  useEffect(() => {
    setPageTitle('Amazon Index Checker');
  }, [location.pathname]);

  // Load sheets config and schedule status when company/product changes
  useEffect(() => {
    const loadSheetsConfig = async () => {
      if (selectedCompany && selectedProduct) {
        const config = await amazonIndexCheckerService.getSheetsConfig(
          selectedCompany,
          selectedProduct.id
        );
        setSheetsConfig(config);
      } else {
        setSheetsConfig(null);
      }
    };
    
    const loadScheduleStatus = async () => {
      if (selectedCompany && selectedProduct) {
        try {
          const status = await amazonIndexCheckerService.getASINScheduleStatus(
            selectedCompany,
            selectedProduct.id
          );
          setAsinScheduleStatus(status);
        } catch (err) {
          console.error('Error loading schedule status:', err);
        }
      } else {
        setAsinScheduleStatus({ isScheduled: false });
      }
    };
    
    loadSheetsConfig();
    loadScheduleStatus();
  }, [selectedCompany, selectedProduct]);

  // Handle ASIN schedule toggle
  const handleToggleASINSchedule = async () => {
    if (!selectedCompany || !selectedProduct || isScheduleToggling) return;
    
    setIsScheduleToggling(true);
    try {
      const newScheduleStatus = !asinScheduleStatus.isScheduled;
      
      if (newScheduleStatus) {
        // Enable scheduling
        await amazonIndexCheckerService.enableASINScheduling(selectedCompany, selectedProduct.id, userEmail!);
      } else {
        // Disable scheduling
        await amazonIndexCheckerService.disableASINScheduling(selectedCompany, selectedProduct.id);
      }
      
      // Update local state
      setAsinScheduleStatus({
        isScheduled: newScheduleStatus,
        nextCheckDate: newScheduleStatus ?
          Timestamp.fromDate(new Date(Date.now() + 90 * 24 * 60 * 60 * 1000)) : // 90 days from now
          undefined
      });
    } catch (error) {
      console.error('Error toggling ASIN schedule:', error);
    } finally {
      setIsScheduleToggling(false);
    }
  };

  const handleNavigate = (to: string) => {
    navigate(to);
    setIsMobileMenuOpen(false);
  };

  // Handle product selection
  const handleProductChange = (value: string) => {
    const product = products.find(p => p.id === value) || null;
    setSelectedProduct(product);
  };

  // Handle ASIN paste input with debounced validation
  const handleAsinPaste = async (value: string) => {
    setPasteAsin(value);
    
    // Clear existing timeout
    if (lookupTimeout) {
      clearTimeout(lookupTimeout);
    }

    if (!value.trim()) {
      setAsinValidationStatus('idle');
      return;
    }

    // Set validating status immediately when user starts typing
    setAsinValidationStatus('validating');

    // Debounce the lookup by 500ms
    const timeoutId = setTimeout(async () => {
      try {
        const result = await pmt2Service.findProductByAsin(value.trim());
        if (result) {
          // ASIN found - auto-populate company and product
          setSelectedCompany(result.company);
          setSelectedProduct(result.product);
          setAsinValidationStatus('valid');
          
          // If in dashboard mode, navigate to product view
          if (isDashboardActive) {
            navigate(`/amazon-index-checker?view=product&company=${encodeURIComponent(result.company)}&asin=${encodeURIComponent(result.product.id)}`);
          }
        } else {
          // ASIN not found in any company
          setAsinValidationStatus('invalid');
        }
      } catch (error) {
        console.error('Error looking up ASIN:', error);
        setAsinValidationStatus('invalid');
      }
    }, 500);

    setLookupTimeout(timeoutId);
  };

  // Clear ASIN lookup timeout on unmount
  useEffect(() => {
    return () => {
      if (lookupTimeout) {
        clearTimeout(lookupTimeout);
      }
    };
  }, [lookupTimeout]);

  // Reset ASIN field when company or product changes externally, or when switching to dashboard mode
  useEffect(() => {
    if (isDashboardActive || isCompanyView) {
      // Clear ASIN field when in dashboard mode or company view
      setPasteAsin('');
      setAsinValidationStatus('idle');
    } else if (selectedProduct && selectedProduct.id !== pasteAsin) {
      setPasteAsin(selectedProduct.id);
      setAsinValidationStatus('valid');
    }
  }, [selectedProduct, isDashboardActive, isCompanyView]);

  // Prepare options for KeyboardNavigableSelect components
  const companyOptions = companies.map(company => ({ value: company, label: company }));
  const productOptions = products.map(product => ({ 
    value: product.id, 
    label: `${product.id}${product.Title ? ` - ${product.Title.substring(0, 30)}${product.Title.length > 30 ? '...' : ''}` : ''}`
  }));

  return (
    <div className="h-screen bg-gray-50 dark:bg-gray-900 flex overflow-hidden amazon-index-checker-app">
      {/* Mobile Menu Button */}
      <div className="md:hidden fixed top-0 left-0 right-0 h-16 bg-white dark:bg-gray-800 z-40 border-b dark:border-gray-700 px-4 flex items-center">
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
        >
          {isMobileMenuOpen ? (
            <X className="h-6 w-6 text-gray-600" />
          ) : (
            <Menu className="h-6 w-6 text-gray-600" />
          )}
        </button>
      </div>

      {/* Mobile Menu Backdrop */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`
          bg-white dark:bg-gray-800 shadow-sm fixed md:relative h-full z-40
          transform transition-transform duration-300 ease-in-out flex flex-col
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0 hidden md:flex'}
          ${isCollapsed && !isMobileMenuOpen && !isFullScreen ? 'w-16 md:w-16' : 'w-64 md:w-64'}
        `}
      >
        {/* Collapse Toggle - Hidden on Mobile */}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="absolute -right-3 top-6 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-full p-1 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 hidden md:block"
          style={{ display: isFullScreen ? 'none' : undefined }}
        >
          <ChevronLeft className={`h-4 w-4 text-gray-600 transition-transform duration-300 ${isCollapsed ? 'rotate-180' : ''}`} />
        </button>

        <div
          className="border-b flex justify-center items-center h-23 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
          onClick={() => handleNavigate('/amazon-index-checker')}
          title={isDashboardActive ? 'Dashboard View' : 'Back to Dashboard'}
        >
          {isCollapsed && !isMobileMenuOpen ? (
            <Search className="h-6 w-6 text-blue-500 mb-1 mt-1 transition-all duration-300" />
          ) : (
            <div className="flex flex-col items-center justify-center py-4">
              <Search className="h-10 w-10 text-blue-500 mb-1" />
              <h1 className="text-lg font-bold text-gray-900 dark:text-white">Index Checker</h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {isDashboardActive ? 'Dashboard Overview' : 'Amazon Indexing Analysis'}
              </p>
            </div>
          )}
        </div>
        
        <nav className="flex-1 mt-4 overflow-y-auto flex flex-col">
          {/* Main Navigation */}
          <div className={`px-4 py-2 mb-4 space-y-2 ${isCollapsed && !isMobileMenuOpen && !isFullScreen ? 'hidden' : 'block'}`}>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-white">
                Select Company
              </label>
              <KeyboardNavigableSelect
                options={companyOptions}
                value={isDashboardActive ? '' : selectedCompany}
                onValueChange={(value) => {
                  if (isDashboardActive) {
                    // If in dashboard mode, navigate to company-selected state
                    setSelectedCompany(value);
                    navigate(`/amazon-index-checker?view=company&company=${encodeURIComponent(value)}`);
                  } else {
                    setSelectedCompany(value);
                  }
                }}
                placeholder={isDashboardActive ? "All Companies" : "Select a company"}
                className="w-full"
                triggerClassName="w-full text-gray-900 dark:text-white bg-white dark:bg-gray-700"
                triggerStyle={{ minHeight: '36px' }}
                contentStyle={{ backgroundColor: 'white', color: 'black', zIndex: 50, paddingLeft: '8px' }}
                itemStyle={{ color: 'black', padding: '8px 12px', paddingLeft: '28px' }}
                disabled={false}
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-white">
                Select Product (ASIN)
              </label>
              <KeyboardNavigableSelect
                options={isDashboardActive ? [] : productOptions}
                value={isDashboardActive || isCompanyView ? '' : (selectedProduct?.id || '')}
                onValueChange={(value) => {
                  if (isDashboardActive) {
                    // This shouldn't happen since options are empty, but handle just in case
                    return;
                  } else if (isCompanyView) {
                    // In company view, navigate to product view with both company and ASIN
                    const product = products.find(p => p.id === value) || null;
                    if (product) {
                      setSelectedProduct(product);
                      navigate(`/amazon-index-checker?view=product&company=${encodeURIComponent(selectedCompany)}&asin=${encodeURIComponent(product.id)}`);
                    }
                  } else {
                    handleProductChange(value);
                  }
                }}
                placeholder={isDashboardActive ? "All Products" : isCompanyView ? "Choose a product..." : "Select a product"}
                className="w-full"
                triggerClassName="w-full text-gray-900 dark:text-white bg-white dark:bg-gray-700"
                disabled={isDashboardActive || !selectedCompany || products.length === 0}
                contentStyle={{ backgroundColor: 'white', color: 'black', zIndex: 50 }}
                itemStyle={{ color: 'black' }}
              />
            </div>

            {/* Paste ASIN Field - moved below product selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-white">
                Paste ASIN
              </label>
              <div className="relative">
                <Input
                  value={pasteAsin}
                  onChange={(e) => handleAsinPaste(e.target.value)}
                  placeholder={isDashboardActive ? "Enter ASIN to switch to product view..." : "Enter or paste ASIN..."}
                  className={`w-full ${
                    asinValidationStatus === 'invalid'
                      ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                      : asinValidationStatus === 'valid'
                      ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                      : 'bg-white dark:bg-gray-700'
                  } text-gray-900 dark:text-white`}
                />
                {asinValidationStatus === 'validating' && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                  </div>
                )}
              </div>
              <div className="mt-1 text-xs">
                <div className="text-gray-600 dark:text-gray-400">
                  {isDashboardActive ? "Enter any ASIN to navigate to product view" : "Example: B075W773MZ"}
                </div>
                {asinValidationStatus === 'invalid' && (
                  <div className="text-red-600 dark:text-red-400">
                    ASIN not found in any company
                  </div>
                )}
                {asinValidationStatus === 'valid' && !isDashboardActive && (
                  <div className="text-green-600 dark:text-green-400">
                    ✓ ASIN found and fields populated
                  </div>
                )}
              </div>
            </div>
            
            {/* Add Terms Buttons - Hide when dashboard is active */}
            {selectedProduct && !isDashboardActive && (
              <div className="mt-6 space-y-2">
                <Button
                  onClick={() => onPasteTermsClickRef && onPasteTermsClickRef()}
                  size="sm"
                  className="w-full"
                  variant="default"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Paste Terms
                </Button>
                <div className="space-y-1">
                  <Button
                    onClick={() => onSheetsTermsClickRef && onSheetsTermsClickRef()}
                    size="sm"
                    className="w-full"
                    variant="outline"
                  >
                    <FileSpreadsheet className="h-4 w-4 mr-2" />
                    {sheetsConfig ? 'Sync Sheets Terms' : 'Import Sheets Terms'}
                  </Button>
                  {sheetsConfig && sheetsConfig.lastSyncedAt && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 text-center">
                      Last Sync: {new Date(sheetsConfig.lastSyncedAt).toLocaleDateString()}
                    </div>
                  )}
                </div>
                
                {/* 3-Month Check Toggle */}
                <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Calendar className="h-4 w-4 text-indigo-500" />
                      <Label htmlFor="asin-schedule" className="text-sm font-medium text-gray-900 dark:text-white">
                        3-Month Check
                      </Label>
                    </div>
                    <Switch
                      id="asin-schedule"
                      checked={asinScheduleStatus.isScheduled}
                      onCheckedChange={handleToggleASINSchedule}
                      disabled={isScheduleToggling}
                    />
                  </div>
                  
                  {asinScheduleStatus.isScheduled && asinScheduleStatus.nextCheckDate && (
                    <div className="mt-2 flex items-center space-x-1 text-xs text-gray-600 dark:text-gray-400">
                      <CheckCircle className="h-3 w-3" />
                      <span>
                        Next: {new Date(asinScheduleStatus.nextCheckDate.toDate()).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                  
                  {isScheduleToggling && (
                    <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                      Updating schedule...
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Dashboard Mode Indicator */}
            {isDashboardActive && (
              <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <div className="text-center">
                  <Search className="h-8 w-8 text-blue-500 mx-auto mb-2" />
                  <h3 className="text-sm font-medium text-blue-900 dark:text-blue-100">Dashboard View</h3>
                  <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                    Viewing all ASINs across companies. Click any ASIN card to switch to product view.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="mx-4 my-4 border-t border-gray-200 dark:border-gray-700" />

          {/* Apps Hub Link */}
          <div
            onClick={() => handleNavigate('/dashboard')}
            className="flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
          >
            <Home className={`${isCollapsed && !isMobileMenuOpen ? '' : 'mr-3'} h-5 w-5`} />
            {(!isCollapsed || isMobileMenuOpen) && 'Apps Hub'}
          </div>
        </nav>

        {/* User Info Section */}
        <UserInfoSection displayName={displayName} />
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 overflow-auto pt-16 md:pt-0 relative">
        <Outlet context={{
          onPasteTermsClick: handleSetOnPasteTermsClick,
          onSheetsTermsClick: handleSetOnSheetsTermsClick,
          sheetsConfig,
          onDashboardStateChange: handleDashboardStateChange
        }} />
      </main>
    </div>
  );
};

export default AmazonIndexLayout;